import { 
    Component
    , OnInit
    , NgZone
    , Input
    , Output
    , EventEmitter
    , AfterViewInit
    , HostListener 
} from '@angular/core';
import { 
    BarcodeReader
    , CodeDetection
    , Configuration
    , SdkError
    , StrichSDK
    , SymbologyName 
} from "@pixelverse/strichjs-sdk";
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { GlobalService } from '../service/global.service';
import { EncryptionService } from '../../services/encryption.service';
import { SecurityService } from 'app/core/security/security.service';
import { SystemMessageService } from 'app/core/system-message/system-message-service';

@Component({
    selector: 'pcg-barcode-scanner-strich',
    templateUrl: './barcode-scanner-strich.component.html',
    styleUrls: ['./barcode-scanner-strich.component.scss'],
    standalone: false
})
export class BarcodeScannerStrichComponent implements OnInit, AfterViewInit{

    @Input() symbology: SymbologyName[] = ['code128'];
    @Input() scanningBtnText = "Start Scanning";
    @Input() showResults = true;
    @Input() overrideSound = false;
    @Input() closeScanAfterScan = true;
    @Input() showHeader = true;
    @Input() height = "18rem"
    @Input() showCloseButton = true;

    @Output() scanResult = new EventEmitter<string>();
    @Output() barcodeReaderOutput = new EventEmitter<BarcodeReader>();

    errorMessage?: string;

    busy = false;
    initialized = false;
    isScan = false;
    isMobile = false;
    isLoaded = false;

    codeDetection?: CodeDetection;
    barcodeReader?: BarcodeReader;
    configuration: Configuration;
    
    beepSuccess = new Audio();

    constructor(
        private ngZone: NgZone
        , public router: Router
        , private encryption: EncryptionService
        , private sec: SecurityService
        , private ms: SystemMessageService
    ) { }

    @HostListener('window:resize')
	onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth);
        this.setConfig();
        this.busy = true;
        let licenseKey;
        if (!this.isLoaded) {
            let encryptedKey = this.sec?.getUser()?.strich!;
            this.encryption.importKey().then((keys) => { 
                if(!encryptedKey) { 
                    this.busy = false;
                    this.errorMessage = `No key found`; 
                    return;
                }
                let productionLicenseArrayBuffer = this.encryption.strToArrayBuffer(encryptedKey);
                this.encryption.decrypt(productionLicenseArrayBuffer, keys, this.encryption.iv).then((decryptedData) => {
                    let enc = new TextDecoder("utf-8");
                    licenseKey = enc.decode(decryptedData);
                    StrichSDK.initialize(licenseKey)
                        .then(() => { console.log('STRICH SDK initialized'); this.isLoaded = true; })
                        .catch(err => { 
                            if (err.toString().includes('The license key is invalid or expired')) { 
                                this.ms.setSystemMessage("Critical Update. Please log out and log back in.", "error");
                            }
                            this.errorMessage = `Failed to initialize Strich SDK (Have you provided a license key?) ${err}`; 
                        })
                        .finally(() => { this.busy = false; });
                });
            });
        } 
        this.router.events.subscribe(() => { this.closeScanning(); }); 
    }

    ngAfterViewInit() { if (this.isMobile) { setTimeout(() => { this.startScanning(); }, 1000); } }

    setConfig() {
        const basePath = document.getElementsByTagName('base')[0].href;
        //this.beepSuccess.src = basePath + 'assets/sounds/GreenScan.mp3';
        this.configuration = {            
            selector: '#scanner'// the CSS selector identifying the host element
            //@ts-ignore
            , debug: true
            , engine: {                    
                symbologies: this.symbology // restrict to the required symbologies
                , minScanlinesNeeded: 3
                , invertedCodes: true
                , duplicateInterval: 1000
            }
            , frameSource: { resolution: 'full-hd' } // full-hd is recommended for more challenging codes 
            , overlay: {
                customLogoSrc: basePath + "assets/images/icons/hurricane-128x128.png"
                , showCameraSelector: true
                , showDetections: true
                , showFlashlight: true
                , showTargetingLine: true
            }
            , locator: {                
                regionOfInterest: { // restrict active area to a horizontal bar in the center
                    left: 0.05
                    , right: 0.05
                    , top: 0.35
                    , bottom: 0.35
                }
            }
            , feedback: { // get audio and vibration feedback for scans               
                audio: this.overrideSound === true ? false : true
                , vibration: true
            }
        };
    }

    removeCodeDetection(): void { this.codeDetection = undefined; }

    startScanning(): void {
        this.isScan = true; 
        setTimeout(() => {
			if (!this.barcodeReader) { 
                // BarcodeReader initialization
                try {
                    const barcodeReader = new BarcodeReader(this.configuration);
                    barcodeReader.initialize().then(result => {
                        this.initialized = true;
                        this.barcodeReader = result;
                        this.barcodeReaderOutput.emit(this.barcodeReader)

                        // register detection hook, run it in the Angular zone so change detection works
                        this.barcodeReader.detected = (detections) => { 
                            this.ngZone.run(() => { 
                                this.codeDetection = detections[0]; 
                                this.processScanResult(this.codeDetection.data);
                            }); 
                        };
            
                        // start reading codes
                        this.barcodeReader.start()
                            .then(() => { })
                            .catch(err => { this.errorMessage = `BarcodeReader failed to start: ${err}`; });
                    })
                    .catch(err => { this.errorMessage = `BarcodeReader failed to initialize: ${err}`; })
                    .finally(() => { 
                        this.busy = false; 
                        this.router.events.subscribe(() => { this.closeScanning(); }); 
                    });
                } catch (err) {
                    if (err instanceof SdkError) { this.errorMessage = err.message; } 
                    else { this.errorMessage = 'An unknown error occurred'; }
                }
            } 
		}, 50);        
    }

    closeScanning() {
        //this.beepSuccess?.remove();
        this.barcodeReader?.stop();
        this.barcodeReader?.destroy();
        this.barcodeReader = null!;
        this.removeCodeDetection();
        this.isScan = false;
        this.isLoaded = false;
    }

    processScanResult(str: string) { 
        //if (this.overrideSound === true) { this.beepSuccess.play(); }
        this.scanResult.emit(str); 
        if (this.closeScanAfterScan) { 
            this.closeScanning(); 
            this.initialized = false;
        } else { this.barcodeReaderOutput.emit(this.barcodeReader); }
    }
}
